import { Component, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import { ApplicationDto, CognitoUserDto, PERMISSION, PermissionDto, UserClient, UserDto, UserPoolInfo } from '@clearing-hub/common';
import { TableColumn } from '@siemens/ngx-datatable/lib/types/table-column.type';
import { ActivatedRoute } from '@angular/router';
import { ColumnMode, DatatableComponent } from '@siemens/ngx-datatable';
import { CreateUserComponent } from './create-user/create-user.component';
import { UpdateUserComponent } from './update-user/update-user.component';
import { BsModalService } from 'ngx-bootstrap/modal';
import { UpdateApplicationAccessesComponent } from './update-application-accesses/update-application-accesses.component';
import { UpdateAttributesComponent } from './update-attributes/update-attributes.component';
import { UserService } from '../../service/user.service';
import { NgxBootstrapConfirmService } from '../ngx-bootstrap-confirm/ngx-bootstrap-confirm.service';
import { ToastrService } from 'ngx-toastr';
import { AuthenticationService } from '../../service/authentication.service';
import { PermissionHandler } from '@clearing-hub/angular-common';
import { SidebarService } from '../../service/sidebar.service';
import { Subscription } from 'rxjs';
import { ChangePasswordComponent } from './change-password/change-password.component';

@Component({
  selector: 'fxch-user-management',
  templateUrl: './user-management.component.html',
  styleUrls: ['./user-management.component.scss']
})
export class UserManagementComponent extends PermissionHandler implements OnInit, OnDestroy {

  @ViewChild('datatableComponent', { static: true }) datatableComponent: DatatableComponent;

  @ViewChild('emailTmpl', { static: true }) emailTmpl: TemplateRef<NgTemplateOutlet>;
  @ViewChild('updateRolesTmpl', { static: true }) updateRolesTmpl: TemplateRef<NgTemplateOutlet>;
  @ViewChild('updateApplicationAccessesTmpl', { static: true }) updateApplicationAccessesTmpl: TemplateRef<NgTemplateOutlet>;
  @ViewChild('deleteUserTmpl', { static: true }) deleteUserTmpl: TemplateRef<NgTemplateOutlet>;
  @ViewChild('enableUserTmpl', { static: true }) enableUserTmpl: TemplateRef<NgTemplateOutlet>;
  @ViewChild('updateUserAttributesTmpl', { static: true }) updateUserAttributesTmpl: TemplateRef<NgTemplateOutlet>;
  @ViewChild('changePasswordTmpl', { static: true }) changePasswordTmpl: TemplateRef<NgTemplateOutlet>;

  @ViewChild('emailCellTmpl', { static: true }) emailCellTmpl: TemplateRef<NgTemplateOutlet>;
  @ViewChild('updateRolesCellTmpl', { static: true }) updateRolesCellTmpl: TemplateRef<NgTemplateOutlet>;
  @ViewChild('updateApplicationAccessesCellTmpl', { static: true }) updateApplicationAccessesCellTmpl: TemplateRef<NgTemplateOutlet>;
  @ViewChild('deleteUserCellTmpl', { static: true }) deleteUserCellTmpl: TemplateRef<NgTemplateOutlet>;
  @ViewChild('updateUserAttributesCellTmpl', { static: true }) updateUserAttributesCellTmpl: TemplateRef<NgTemplateOutlet>;
  @ViewChild('changePasswordCellTmpl', { static: true }) changePasswordCellTmpl: TemplateRef<NgTemplateOutlet>;
  @ViewChild('enableUserCellTmpl', { static: true }) enableUserCellTmpl: TemplateRef<NgTemplateOutlet>;

  applications: Array<ApplicationDto>;
  // roles: Array<RoleDto>;
  groups: Array<string>;
  permissions: Array<PermissionDto>;

  columns: Array<TableColumn>;

  ColumnMode = ColumnMode;

  users: Array<CognitoUserDto>;
  enabledAttributes: Array<string>;
  bit = true;

  user: UserClient;
  PERMISSION = PERMISSION;

  userPoolInfo: UserPoolInfo;

  private subscriptions: Array<Subscription> = new Array<Subscription>();

  constructor(private readonly activatedRoute: ActivatedRoute,
              private readonly modalService: BsModalService,
              private readonly userService: UserService,
              private readonly authenticationService: AuthenticationService,
              private readonly sidebarService: SidebarService,
              private readonly ngxBootstrapConfirmService: NgxBootstrapConfirmService,
              private readonly toastrService: ToastrService) {
    super(authenticationService.getUserPermissions())
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(s => s.unsubscribe());
  }

  ngOnInit(): void {
    this.user = AuthenticationService.getUser();
    this.userPoolInfo = this.activatedRoute.snapshot.data['userPoolInfo'];
    this.users = this.activatedRoute.snapshot.data['preload'].users;
    this.applications = this.activatedRoute.snapshot.data['preload'].applications;
    // this.roles = this.activatedRoute.snapshot.data['preload'].roles;
    this.groups = this.activatedRoute.snapshot.data['preload'].groups;
    this.enabledAttributes = this.activatedRoute.snapshot.data['preload'].enabledAttributes;

    this.initTable();
    this.subscriptions.push(
      this.sidebarService.toggleSidebar.subscribe(() => {
        // TODO do it with transition
        setTimeout(() => {
          this.datatableComponent.recalculate();
        }, 310);
      })
    );
  }

  initTable() {
    this.columns = [
      {
        headerTemplate: this.emailTmpl,
        cellTemplate: this.emailCellTmpl,
        name: 'Email',
        prop: 'email',
      }
    ];
    if (this.user.permissions.includes(PERMISSION.USER_MANAGEMENT_UPDATE_USER_ROLES)) {
      this.columns.push({
        headerTemplate: this.updateRolesTmpl,
        cellTemplate: this.updateRolesCellTmpl,
        name: 'Update roles',
        width: 150,
        maxWidth: 200,
        sortable: false,
        headerClass: 'text-center',
      });
    }
    if (this.user.permissions.includes(PERMISSION.USER_MANAGEMENT_UPDATE_USER_ATTRIBUTES)) {
      this.columns.push({
        headerTemplate: this.updateUserAttributesTmpl,
        cellTemplate: this.updateUserAttributesCellTmpl,
        name: 'Update user attributes',
        width: 150,
        maxWidth: 200,
        sortable: false,
        headerClass: 'text-center',
      });
    }
    if (this.user.permissions.includes(PERMISSION.USER_MANAGEMENT_CHANGE_USER_PASSWORD)) {
      this.columns.push({
        headerTemplate: this.changePasswordTmpl,
        cellTemplate: this.changePasswordCellTmpl,
        name: 'Change password',
        width: 150,
        maxWidth: 200,
        sortable: false,
        headerClass: 'text-center'
      });
    }
    if (this.user.permissions.includes(PERMISSION.USER_MANAGEMENT_DELETE_USER)) {
      this.columns.push({
        headerTemplate: this.deleteUserTmpl,
        cellTemplate: this.deleteUserCellTmpl,
        name: 'Delete user',
        width: 150,
        maxWidth: 200,
        sortable: false,
        headerClass: 'text-center'
      });
    }
    // if (this.user.permissions.includes(PERMISSION.USER_MANAGEMENT_DELETE_USER)) {
    //   this.columns.push({
    //     headerTemplate: this.enableUserTmpl,
    //     cellTemplate: this.enableUserCellTmpl,
    //     name: 'Enable user',
    //     width: 150,
    //     maxWidth: 200,
    //     sortable: false,
    //     headerClass: 'text-center'
    //   });
    // }
  }

  openUpdateApplicationAccessesModal(user: UserDto) {
    const initialState: Partial<UpdateApplicationAccessesComponent> = {
      user: user,
      groups: this.groups
    }
    this.modalService.show(UpdateApplicationAccessesComponent, {
      initialState
    });
  }

  openUpdateModal(u: UserDto) {
    this.userService.getUserRoles({username: u.email}).subscribe((roles) => {
      const initialState = {
        user: u,
        // roles: this.roles,
        applications: this.applications,
        userRoles: roles
      };
      this.modalService.show(UpdateUserComponent, {
        initialState
      });
    });
  }

  openUpdateUserAttributes(u: CognitoUserDto) {
    const initialState = {
      user: u,
      enabledAttributes: this.enabledAttributes
    }
    const modal = this.modalService.show(UpdateAttributesComponent, {
      initialState,
      class: 'modal-lg'
    });
    modal.content.onUpdate.subscribe((event) => {
      this.users = this.users.map(item => {
        if (u.id === item.id) {
          return {
            ...item,
            attributes: event
          }
        }
        return item;
      });
    });
  }

  openCreateModal() {
    const initialState = {
      applications: this.applications
    }
    const modal = this.modalService.show(CreateUserComponent, {
      initialState
    });

    modal.content.onUserCreate.subscribe((user) => {
      this.users = [...this.users, user];
    });
  }

  openChangePasswordModal(user) {
    console.log(user)
    const initialState = {
      user: user,
      userPoolInfo: this.userPoolInfo
    }
    this.modalService.show(ChangePasswordComponent, {
      initialState
    });
  }


  deleteUser(u: UserDto) {
    const options = {
      title: 'Are you sure you want to delete this user?',
      confirmLabel: 'Ok',
      declineLabel: 'Cancel'
    }

    this.ngxBootstrapConfirmService.confirm(options).then((res: boolean) => {
      if (res) {
        this.userService.deleteUser(u.email).subscribe((resp) => {
          console.log(resp);
          this.users = this.users.filter(user => user.id !== u.id);
          this.toastrService.success('Successfully deleted');
        }, err => {
          this.toastrService.error(err.error.message);
        });
      }
    });
  }
  // enableUser(u: UserDto) {
  //   const options = {
  //     title: 'Are you sure you want to enable this user?',
  //     confirmLabel: 'Ok',
  //     declineLabel: 'Cancel'
  //   }
  //
  //   this.ngxBootstrapConfirmService.confirm(options).then((res: boolean) => {
  //     if (res) {
  //       this.userService.setUserStatus(u.email, true).subscribe((resp) => {
  //         this.users = this.users.filter(user => user.id !== u.id);
  //         this.toastrService.success('Successfully deleted');
  //       }, err => {
  //         this.toastrService.error(err.error.message);
  //       });
  //     }
  //   });
  // }
}
